import { Spinner } from '@/components/_common';
import TableContainer from '@/components/_common/Table/TableContainer';
import { useQuery } from '@/components/_hooks/useQuery';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { selectCurrentTableKey } from '@/redux/selectors/dataTableSelectors';
import { selectOutputVersionOptions } from '@/redux/selectors/orderPlacementSelectors';
import { selectActiveTitleId } from '@/redux/selectors/titleSelectors';
import { selectIsActiveTitleLoading } from '@/redux/selectors/titleV2Selectors';
import { applyFilters, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import styled from '@emotion/styled';
import { Grid, Typography } from '@mui/material';
import { useEffect, useMemo } from 'react';

interface TitleDetailAvailsV2Props {
  lookupId: number;
  isActiveTab: boolean;
}

const TitleDetailAvailsV2 = ({ lookupId, isActiveTab }: TitleDetailAvailsV2Props) => {
  const dispatch = useAppDispatch();
  const currentTable = useAppSelector(selectCurrentTableKey);
  const query = useQuery();
  const versionId = query.get('version_id');
  const activeTitleId = useAppSelector(selectActiveTitleId);
  const outputVersionOptions = useAppSelector(selectOutputVersionOptions);
  const versions = outputVersionOptions[lookupId] || [];
  const titleLoading = useAppSelector(selectIsActiveTitleLoading);
  const version = useMemo(() => {
    if (!versionId || versionId === 'all') return undefined;

    return versions?.find((v) => String(v.id) === String(versionId));
  }, [versionId, versions]);

  useEffect(() => {
    if (titleLoading || !isActiveTab || !versionId) return;

    dispatch(setCurrentTable('titleDetailAvails'));
    dispatch(clearFilters(undefined));

    const filters: Record<string, string[] | undefined> = {
      version_hid_filter: undefined
    };
    const foundVersion = versions.find((ver) => String(ver.id) === versionId);
    filters.version_hid_filter = foundVersion?.hid ? [foundVersion.hid] : undefined;

    if (foundVersion?.hid) dispatch(applyFilters({ filters }));
  }, [versions, versionId, activeTitleId, titleLoading, isActiveTab, version]);

  if (!version) {
    return (
      <Grid item sx={{ p: 6 }}>
        <StyledSelectVersionText variant="h6">Please Select a Version to View Avails</StyledSelectVersionText>
      </Grid>
    );
  }

  return !titleLoading && currentTable === 'titleDetailAvails' ? (
    <StyledTableContainer simplified />
  ) : (
    <Grid item sx={{ p: 6 }}>
      <Spinner contained />
    </Grid>
  );
};

export default TitleDetailAvailsV2;

const StyledSelectVersionText = styled(Typography)`
  text-align: center;
`;

const StyledTableContainer = styled(TableContainer)`
  .MuiTableHead-root {
    background: ${({ theme }) => theme.palette.background.paper};
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }

  #overflow {
    background: ${({ theme }) => theme.palette.background.paper};

    > span:nth-of-type(2) {
      background: ${({ theme }) => `linear-gradient(to left, ${theme.palette.background.paper}, transparent 75%)`};
    }
  }
`;
